import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { IInsertMediaUploadItem } from '@fi-shared/fi-insert-media/fi-insert-media';
import { FiImageSize, FileType } from '@fi-shared/types';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { fiGetMediaBankFileIconAwesome } from '@fi-shared/utils';
import { AppTextService } from '@fi-shared/app-text';
import { MediaBankStorageType } from '@fi-shared/media-bank/media-bank-storage-type';

@Component({
    selector: 'fi-insert-media-details-grid-view',
    templateUrl: './fi-insert-media-details-grid-view.component.html',
    styleUrls: ['./fi-insert-media-details-grid-view.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-insert-media-details-grid-view' },
    standalone: false,
})
export class FiInsertMediaDetailsGridViewComponent {
    @Input() items: IInsertMediaUploadItem[] = [];
    @Input() uploads: IInsertMediaUploadItem[] = [];
    @Input() checkedItems: IInsertMediaUploadItem[] = [];
    @Input() isBrandedColorIcon: boolean;
    @Input() allowMultiSelect: boolean;
    @Input() allowStoreInMyFiles: boolean;
    @Input() loading: boolean;
    @Output() selectionChange = new EventEmitter<IInsertMediaUploadItem[]>();
    @Output() onItemDelete = new EventEmitter<IInsertMediaUploadItem>();

    constructor(
        private mediaStorageService: FiMediaStorageService,
        private appTextService: AppTextService,
    ) {}

    isItemSelected(mediaItem: IInsertMediaUploadItem): boolean {
        return (
            this.checkedItems.findIndex((item) => {
                return item.mediaBankFile.Id === mediaItem.mediaBankFile.Id;
            }) !== -1
        );
    }

    onGridItemClick(mediaItem: IInsertMediaUploadItem) {
        if (mediaItem.percentageLoaded$?.value < 100) {
            return;
        }
        if (this.allowMultiSelect) {
            if (this.isItemSelected(mediaItem)) {
                const itemIndex = this.checkedItems.findIndex((item) => {
                    return item.mediaBankFile.Id === mediaItem.mediaBankFile.Id;
                });
                this.checkedItems.splice(itemIndex, 1);
            } else {
                this.checkedItems.push(mediaItem);
            }
        } else {
            this.checkedItems = [];
            this.checkedItems.push(mediaItem);
        }
        this.selectionChange.emit(this.checkedItems);
    }

    getImage(item: IInsertMediaUploadItem): string {
        const mediaBankFile = item.mediaBankFile,
            fileType = mediaBankFile.FileType,
            storageType = item.mediaBankFile.StorageType;
        if (storageType === MediaBankStorageType.Vimeo) {
            return this.mediaStorageService.getMediaFileUrlByFileName(
                mediaBankFile.PhysicalFileName + mediaBankFile.Extension,
                FiImageSize.Small,
                mediaBankFile.ChangedOn,
            );
        }
        if (storageType === MediaBankStorageType.Azure && [FileType.Image, FileType.Pdf].includes(fileType)) {
            return this.mediaStorageService.getMediaFileUrl(mediaBankFile, { size: FiImageSize.Small });
        }
    }

    deleteItem(item: IInsertMediaUploadItem) {
        if (item.percentageLoaded$.value < 100) {
            item.abortUpload();
        } else {
            this.onItemDelete.emit(item);
        }
    }

    getIconClass(item: IInsertMediaUploadItem): string {
        return fiGetMediaBankFileIconAwesome(item.mediaBankFile.FileType);
    }

    getGridItemClass(item: IInsertMediaUploadItem) {
        return {
            selected: this.isItemSelected(item),
            BrandedBackgroundColor: !this.getImage(item) && !this.isBrandedColorIcon,
            BrandedColor: !this.getImage(item) && this.isBrandedColorIcon,
        };
    }

    protected getUploadStatusLabel(percentageLoaded: number): string {
        return this.appTextService.get(percentageLoaded < 100 ? 'upload_status.uploading' : 'checklist.complete');
    }

    protected normalizePercentageLoaded(percentageLoadedUnknownType: any): number {
        return percentageLoadedUnknownType as number;
    }
}
